import Phaser from "phaser";
import { ModalLayer, NineSlicePanel } from "@yjsgames/phaser-ui";
import { CONTENT_PANEL_STYLE, FONT_FAMILY, TYPO } from "./uiTokens.js";
import { drawRewardShapeIcon } from "./rewardShapeIcon.js";
import type { RewardShape } from "../systems/rewardLineItems.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
import { setExpandedCloseButtonHitArea } from "./closeButtonHitArea.js";

/** 상세 표시 모델 — t() 적용 완료 (GameScene이 생성). */
export interface RewardDetailModel {
  readonly title: string;
  readonly tint: string;
  readonly iconKey: string;
  /** 모듈일 때 실제 조각 모양 — 있으면 iconKey image 대신 파이프 모양 Graphics로 그린다. */
  readonly shape?: RewardShape;
  /** 본문 줄 — 모듈=등급/슬롯/설명, 재화=설명문. */
  readonly lines: string[];
}

export interface RewardDetailModalOptions {
  readonly screenWidth: number;
  readonly screenHeight: number;
  readonly model: RewardDetailModel;
  /** 상단 헤더 제목 — 있으면 프레임 상단을 확장해 제목 행 표기, 없으면 기존 레이아웃 그대로. */
  readonly headerTitle?: string;
  /** 닫기 — 획득 팝업으로 복귀(획득 모달은 유지). */
  readonly onClose: () => void;
}

/**
 * 보상 상세 팝업 — 획득 팝업(depth 2000) 위에 depth 2100으로 겹쳐 띄운다(overlayAlpha 0.5로 뒤가 비침).
 * 큰 아이콘 + 제목(등급색) + 본문(모듈=등급/슬롯/설명, 재화=설명문) + ✕. 닫으면 획득 팝업으로 복귀.
 * 단일 파츠 획득 직행(그리드 생략) 시 headerTitle("보상 획득!")이 상단 확장 행에 표기된다.
 */
export class RewardDetailModal extends ModalLayer {
  constructor(scene: Phaser.Scene, opts: RewardDetailModalOptions) {
    super(scene, {
      screenWidth: opts.screenWidth,
      screenHeight: opts.screenHeight,
      overlayColor: PALETTE_NUM.overlayDim,
      overlayAlpha: 0.5,
      depth: 2100,
      // dim(팝업 밖) 클릭으로도 닫기 — 패널 영역은 아래 setBackdropCloseExclusion으로 제외.
      onBackdropClick: () => opts.onClose(),
    });

    const m = opts.model;
    const cx = opts.screenWidth / 2;
    const cy = opts.screenHeight / 2;
    const pw = 240;
    // 헤더가 있으면 프레임 상단을 그만큼 확장 — 이하 본문 요소는 headerH만큼 내려 기존 간격 유지.
    const headerH = opts.headerTitle ? 34 : 0;
    const ph = 220 + headerH;
    const top = cy - ph / 2;

    const panel = new NineSlicePanel(scene, cx, cy, {
      style: CONTENT_PANEL_STYLE,
      width: pw,
      height: ph,
      originX: 0.5,
      originY: 0.5,
    });
    this.contentContainer.add(panel);
    this.setBackdropCloseExclusion(cx - pw / 2, top, pw, ph);

    const closeBtn = scene.add
      .text(cx + pw / 2 - 16, top + 16, "✕", {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.title,
        color: PALETTE.textSecondary,
      })
      .setOrigin(0.5);
    setExpandedCloseButtonHitArea(closeBtn);
    closeBtn.on("pointerup", () => opts.onClose());
    this.contentContainer.add(closeBtn);

    if (opts.headerTitle) {
      // 획득 그리드 팝업 제목과 동일 톤 (TYPO.title + accentGold) — 직행 팝업에서도 "획득" 맥락 유지.
      const header = scene.add
        .text(cx, top + 18, opts.headerTitle, {
          fontFamily: FONT_FAMILY,
          fontSize: TYPO.title,
          color: PALETTE.accentGold,
        })
        .setOrigin(0.5);
      this.contentContainer.add(header);
    }

    // 모듈 + 모양 정보가 있으면 실제 조각 파이프 모양(등급색), 아니면 기존 획일 아이콘.
    const icon = m.shape
      ? drawRewardShapeIcon(scene, cx, top + headerH + 50, 48, m.shape, parseInt(m.tint.replace("#", ""), 16))
      : scene.add.image(cx, top + headerH + 50, m.iconKey).setDisplaySize(48, 48);
    this.contentContainer.add(icon);

    const title = scene.add
      .text(cx, top + headerH + 90, m.title, {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.large,
        color: m.tint,
      })
      .setOrigin(0.5);
    this.contentContainer.add(title);

    const body = scene.add
      .text(cx, top + headerH + 116, m.lines.join("\n"), {
        fontFamily: FONT_FAMILY,
        fontSize: TYPO.caption,
        color: PALETTE.textSecondary,
        align: "center",
        lineSpacing: 4,
        wordWrap: { width: pw - 28 },
      })
      .setOrigin(0.5, 0);
    this.contentContainer.add(body);
  }
}
